import type { PlayerRewardParams } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { Env } from '@/consts/Env';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useProgressRouterIntel } from '@/hooks/useProgressRouter';
import { AppRoutes } from '@/i18n/constants';
import handleCopyText from '@/libs/handleCopyText';
import { MODAL_DATA_PARAM, MODAL_PARAM } from '@/modals/consts';
import openModal from '@/modals/utils/openModal';
import { playerGamificationRewardsOptions } from '@/queries/playerGamificationRewards/playerGamificationRewards.queries';
import { GamificationRewardType } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import { useUserMeQuery } from '@/queries/user/user.queries';

type StatusRewardScratchCardVariant = 'level-up' | 'status-up';
const LEVEL_UP_REWARD_CLAIM_BATCH_SIZE = 10;

type ShareStatusData = {
  id?: string;
  name?: string;
  levelNumber?: number;
};

const statusUpParams: PlayerRewardParams = {
  filter: {
    type: GamificationRewardType.StatusUp,
    claimedAt: { $isnull: true },
  },
  limit: 1,
  sort: { createdAt: 'asc' },
};

const levelUpParams: PlayerRewardParams = {
  filter: {
    type: GamificationRewardType.LevelUp,
    claimedAt: { $isnull: true },
  },
  limit: LEVEL_UP_REWARD_CLAIM_BATCH_SIZE,
  sort: { createdAt: 'asc' },
};

export const useManageStatusUpActions = (scratchCardVariant: StatusRewardScratchCardVariant = 'status-up') => {
  const t = useTranslations('gamification.scratch-card-statuses');
  const queryClient = useQueryClient();
  const router = useProgressRouterIntel();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const tShare = useTranslations('scratch-card');
  const { data: user } = useUserMeQuery();

  const resolveRewardIds = useCallback(async () => {
    const data = await queryClient.fetchQuery(
      playerGamificationRewardsOptions.meRewards(
        scratchCardVariant === 'status-up' ? statusUpParams : levelUpParams,
        { staleTime: 0 },
      ),
    );

    return data.items.map(r => r.id);
  }, [queryClient, scratchCardVariant]);

  const handleScratchAction = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    const rewardIds = await resolveRewardIds();

    if (!rewardIds.length) {
      return;
    }

    if (isMobile) {
      const scratchCardParams = new URLSearchParams({
        variant: scratchCardVariant,
        userId: user.id,
      });

      scratchCardParams.set('rewardIds', rewardIds.join(','));

      router.push(`${AppRoutes.scratchCard}?${scratchCardParams.toString()}`);
      return;
    }

    openModal('scratch-card', {
      userId: user.id,
      variant: scratchCardVariant,
      rewardIds,
    });
  }, [isMobile, resolveRewardIds, router, scratchCardVariant, user?.id]);

  const getStatusShareUrl = (statusName?: string): string => {
    const shareUrl = new URL(Env.NEXT_PUBLIC_APP_URL ?? window.location.origin);

    if (statusName) {
      shareUrl.searchParams.set(MODAL_PARAM, 'gsc');
      shareUrl.searchParams.set(MODAL_DATA_PARAM, statusName);
    }

    return shareUrl.toString();
  };

  const getLevelShareUrl = (levelNumber?: number): string => {
    const shareUrl = new URL(Env.NEXT_PUBLIC_APP_URL ?? window.location.origin);

    if (levelNumber != null) {
      shareUrl.searchParams.set(MODAL_PARAM, 'glc');
      shareUrl.searchParams.set(MODAL_DATA_PARAM, String(levelNumber));
    }

    return shareUrl.toString();
  };

  const shareStatus = async (statusName?: string) => {
    const shareText = statusName ? t('share-modal.share-status-text', { status: statusName }) : '';
    const message = `${shareText} ${getStatusShareUrl(statusName)}`.trim();

    try {
      await navigator.share({ text: message });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      handleCopyText('success', message, tShare('your-share-link-has-been-copied'), 'items-center px-6', true);
    }
  };

  const shareLevel = async (levelNumber?: number) => {
    const shareLink = getLevelShareUrl(levelNumber);
    const message = levelNumber
      ? t('share-modal.share-level-text', { levelNumber, link: shareLink })
      : shareLink;

    try {
      await navigator.share({ text: message });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      handleCopyText('success', message, tShare('your-share-link-has-been-copied'), 'items-center px-6', true);
    }
  };

  const handleShareAction = async (type: 'status' | 'level', statusData?: ShareStatusData) => {
    if (type === 'status') {
      openModal('gm-status-share', { statusId: statusData?.id, statusName: statusData?.name });
      return;
    }

    openModal('gm-status-share', {
      levelNumber: statusData?.levelNumber,
      type: 'level',
    });
  };

  const handleReachAction = () => {
    // go to missions page to show user what they need to do to reach the next status
    router.push(AppRoutes.gamificationMissions);
  };

  return {
    handleShareAction,
    handleReachAction,
    handleScratchAction,
    shareLevel,
    shareStatus,
  };
};
